import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, api } from './api';
import { useAuth } from './auth';

export type StaffRole = 'support' | 'developer' | 'admin';

interface StaffState {
  ready: boolean;
  isPlatformAdmin: boolean;
  staffRole: StaffRole | null;
  error: unknown;
}

interface AdminMeWire {
  isPlatformAdmin: boolean;
  staffRole: StaffRole | null;
}

const CLOSED: StaffState = {
  ready: true,
  isPlatformAdmin: false,
  staffRole: null,
  error: null,
};

const LOADING: StaffState = {
  ready: false,
  isPlatformAdmin: false,
  staffRole: null,
  error: null,
};

const Ctx = createContext<StaffState | null>(null);
const cache = new Map<string, StaffState>();
const requests = new Map<string, Promise<StaffState>>();

function toStaffState(wire: AdminMeWire): StaffState {
  return {
    ready: true,
    isPlatformAdmin: wire.isPlatformAdmin,
    staffRole: wire.staffRole,
    error: null,
  };
}

function loadStaff(sessionKey: string, accessToken: string): Promise<StaffState> {
  const cached = cache.get(sessionKey);
  if (cached) return Promise.resolve(cached);

  const existing = requests.get(sessionKey);
  if (existing) return existing;

  const request = api<AdminMeWire>('/admin/me', { accessToken })
    .then(toStaffState)
    .catch(
      (e): StaffState => ({
        ...CLOSED,
        error: e instanceof ApiError ? e : 'unknown',
      }),
    )
    .then((state) => {
      cache.set(sessionKey, state);
      requests.delete(sessionKey);
      return state;
    });
  requests.set(sessionKey, request);
  return request;
}

export function StaffProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, sessionKey } = useAuth();
  const [state, setState] = useState<StaffState>(sessionKey ? LOADING : CLOSED);

  useEffect(() => {
    let active = true;

    if (!sessionKey) {
      setState(CLOSED);
      return () => {
        active = false;
      };
    }

    const cached = cache.get(sessionKey);
    if (cached) {
      setState(cached);
      return () => {
        active = false;
      };
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      setState(CLOSED);
      return () => {
        active = false;
      };
    }

    setState(LOADING);
    void loadStaff(sessionKey, accessToken).then((next) => {
      if (active) setState(next);
    });

    return () => {
      active = false;
    };
  }, [sessionKey, getAccessToken]);

  const value = useMemo(
    () => ({
      ready: state.ready,
      isPlatformAdmin: state.isPlatformAdmin,
      staffRole: state.staffRole,
      error: state.error,
    }),
    [state],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useStaff(): StaffState {
  const v = useContext(Ctx);
  return v ?? CLOSED;
}
